// Shared constants used by both web-app and web-server

export const DEVICE_TYPE_ID__PRINTER = 7;
export const DEVICE_TYPE_ID__FILE_STORE = 8;
export const CORE_SIZE__SINGLEMODE = 'CORE_SIZE__SINGLEMODE';
export const CORE_SIZE__MULTIMODE_50 = 'CORE_SIZE__MULTIMODE_50';
export const CORE_SIZE__MULTIMODE_62_5 = 'CORE_SIZE__MULTIMODE_62_5';
// These constants reflect the names used for the test plan template database table
export const TEST_PLAN_TEMPLATE__SIMPLEX = 'Simplex';
export const TEST_PLAN_TEMPLATE__DUPLEX = 'Duplex';
export const TEST_PLAN_TEMPLATE__MULTIFIBER = 'Multifiber';
export const TEST_PLAN_TEMPLATE__FAN_OUT = 'Fan-out';
export const TEST_BLOCK_TYPE__RL1 = 'RL1';
export const TEST_BLOCK_TYPE__IL_RL = 'IL/RL';

export const TEST_BLOCK_TYPE__PT1 = 'PT1';
export const TEST_BLOCK_TYPE__Polarity = 'Polarity';
export const MIN_INTERNAL_SWITCH_CHANNEL = 1;
export const MAX_INTERNAL_SWITCH_CHANNEL = 2;
export const MIN_EXTERNAL_SWITCH_CHANNEL = 1;
export const MAX_EXTERNAL_SWITCH_CHANNEL = 360;
export const POLARITY_TYPE__A = 'POLARITY_TYPE__A';
export const POLARITY_TYPE__B = 'POLARITY_TYPE__B';
export const POLARITY_TYPE__C = 'POLARITY_TYPE__C';

export const Polarity = {
    TYPE_A: POLARITY_TYPE__A,
    TYPE_B: POLARITY_TYPE__B,
    TYPE_C: POLARITY_TYPE__C
} as const;

export type PolarityTypes = typeof Polarity[keyof typeof Polarity];

export const ASSEMBLY_TYPE__SINGLE_CABLE = 'ASSEMBLY_TYPE__SINGLE_CABLE';
export const ASSEMBLY_TYPE__MULTI_CABLE = 'ASSEMBLY_TYPE__MULTI_CABLE';
export const ASSEMBLY_LENGTH__LT_100_M = 'ASSEMBLY_LENGTH__LT_100_M';
export const ASSEMBLY_LENGTH__LT_500_M = 'ASSEMBLY_LENGTH__LT_500_M';
export const ASSEMBLY_LENGTH__LT_1500_M = 'ASSEMBLY_LENGTH__LT_1500_M';
export const ASSEMBLY_LENGTH__LT_4000_M = 'ASSEMBLY_LENGTH__LT_4000_M';
export const RL_MODE__STANDARD = 'STANDARD';
export const RL_MODE__FAST = 'FAST';
export const MTJ__SINGLE_END_A = 'MTJ__SINGLE_END_A';
export const MTJ__SINGLE_END_B = 'MTJ__SINGLE_END_B';
export const MTJ__SINGLE_END_A_THEN_END_B = 'MTJ__SINGLE_END_A_THEN_END_B';
export const MTJ__DOUBLE = 'MTJ__DOUBLE';
export const TJ_TYPE__MEASURE = 'TJ_TYPE__MEASURE';
export const TJ_TYPE__AUTO = 'TJ_TYPE__AUTO';
export const TJ_TYPE__MANUAL = 'TJ_TYPE__MANUAL';
export const TJ_TYPE__USER_INPUT = 'TJ_TYPE__USER_INPUT';
export const EQUIPMENT_TYPE__RL1 = 'EQUIPMENT_TYPE__RL1';
export const EQUIPMENT_TYPE__PT1 = 'EQUIPMENT_TYPE__PT1';

export const REAL_TIME_DISPLAY__FULL = 'REAL_TIME_DISPLAY__FULL';
export const REAL_TIME_DISPLAY__FAST = 'REAL_TIME_DISPLAY__FAST';
export const REAL_TIME_DISPLAY__DISABLE = 'REAL_TIME_DISPLAY__DISABLE';

export const RESULTS__FULL = 'RESULTS__FULL';
export const RESULTS__PASS_FAIL_ONLY = 'RESULTS__PASS_FAIL_ONLY';

export const SCANNED_SERIAL_NUMBER = 'scanned';
export const AUTOGEN_SERIAL_NUMBER = 'autogen';

export const RETRY_COUNT__NO_LIMIT = -1;

export const TERMINATION_TYPE__CONNECTOR = 'TERMINATION_TYPE__CONNECTOR'; // Deprecated - use more specific types below
export const TERMINATION_TYPE__BARE_FIBER = 'TERMINATION_TYPE__BARE_FIBER';
export const TERMINATION_TYPE__FLAT_CONNECTOR = 'TERMINATION_TYPE__FLAT_CONNECTOR';
export const TERMINATION_TYPE__ANGLED_CONNECTOR = 'TERMINATION_TYPE__ANGLED_CONNECTOR';
export const TERMINATION_TYPE__NOT_SPECIFIED = 'TERMINATION_TYPE__NOT_SPECIFIED';

export const REFERENCE_ORDER__CHANNEL = 'Channel';
export const REFERENCE_ORDER__TEST_TYPE = 'testType';

export const TEST_STATUS_FILTER_VALUE__PASS = 'Pass';
export const TEST_STATUS_FILTER_VALUE__FAIL = 'Fail';
export const TEST_STATUS_FILTER_VALUE__INCOMPLETE = 'Incomplete';

export const TEST_STATUS__PASS = 'TEST_STATUS__PASS';
export const TEST_STATUS__FAIL = 'TEST_STATUS__FAIL';
export const TEST_STATUS__INCOMPLETE = 'TEST_STATUS__INCOMPLETE';

export const NEXT_CABLE = 'Next cable';
export const FLIP_ASSEMBLY = 'Flip assembly';
// TODO: Remove the need for this
export const TEST_PLAN_TEMPLATES = [
    {
        id: 1,
        name: 'Simplex'
    },
    {
        id: 2,
        name: 'Duplex'
    },
    {
        id: 3,
        name: 'Multifiber'
    },
    {
        id: 4,
        name: 'Fan-out'
    }
];
// TODO: Remove the need for this
export const TEST_PLAN_TEMPLATES_BY_ID = {
    1: {
        id: 1,
        name: 'Simplex'
    },
    2: {
        id: 2,
        name: 'Duplex'
    },
    3: {
        id: 3,
        name: 'Multifiber'
    },
    4: {
        id: 4,
        name: 'Fan-out'
    }
};

export const ASSEMBLY_TYPES = {
    SINGLE_CABLE: ASSEMBLY_TYPE__SINGLE_CABLE,
    MULTI_CABLE: ASSEMBLY_TYPE__MULTI_CABLE
} as const;

export const ASSEMBLY_TYPE_LABELS = {
    [ASSEMBLY_TYPES.SINGLE_CABLE]: 'Single cable',
    [ASSEMBLY_TYPES.MULTI_CABLE]: 'Multi cable'
} as const;

export type AssemblyTypes = typeof ASSEMBLY_TYPES[keyof typeof ASSEMBLY_TYPES];

export const MTJ_CONFIGURATIONS = {
    SINGLE_END_A: MTJ__SINGLE_END_A,
    SINGLE_END_B: MTJ__SINGLE_END_B,
    SINGLE_END_A_THEN_END_B: MTJ__SINGLE_END_A_THEN_END_B,
    DOUBLE: MTJ__DOUBLE
} as const;

export const MTJ_CONFIGURATION_LABELS = {
    [MTJ_CONFIGURATIONS.SINGLE_END_A]: 'Single MTJ (End A)',
    [MTJ_CONFIGURATIONS.SINGLE_END_B]: 'Single MTJ (End B)',
    [MTJ_CONFIGURATIONS.SINGLE_END_A_THEN_END_B]: 'Single MTJ (Both Ends)',
    [MTJ_CONFIGURATIONS.DOUBLE]: 'Dual MTJ'
} as const;

export type MtjConfigurations = typeof MTJ_CONFIGURATIONS[keyof typeof MTJ_CONFIGURATIONS];

export const CORE_SIZES = {
    SINGLEMODE: CORE_SIZE__SINGLEMODE,
    MULTIMODE_50: CORE_SIZE__MULTIMODE_50,
    MULTIMODE_62_5: CORE_SIZE__MULTIMODE_62_5
} as const;

export const CORE_SIZE_LABELS = {
    [CORE_SIZES.SINGLEMODE]: 'Singlemode 9µm',
    [CORE_SIZES.MULTIMODE_50]: 'Multimode 50µm',
    [CORE_SIZES.MULTIMODE_62_5]: 'Multimode 62.5µm'
} as const;

export type CoreSizes = typeof CORE_SIZES[keyof typeof CORE_SIZES];

// Cable assembly export result options
export const CABLE_ASSEMBLY_EXPORT__PASS_ONLY = 'PassOnly';
export const CABLE_ASSEMBLY_EXPORT__ALL = 'All';